import { CdkCopyToClipboard } from '@angular/cdk/clipboard'
import { NgClass } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core'

import { TooltipModule } from 'primeng/tooltip'

import { FlxButtonModule, FlxIconModule, FlxModalService } from '@flowx/angular-ui-toolkit'
import { FlxBuildTagComponent } from '@flowx/designer/modules/projects/components/build-tag/build-tag.component'
import {
  ExportBinariesComponent,
  ExportModalData,
} from '@flowx/designer/modules/projects/components/export-binaries/export-binaries.component'
import { FlxDateFormatPipe } from '@flowx/designer/pipes/flx-date.pipe'
import { FlxUserInitialsPipe } from '@flowx/designer/pipes/user-initials.pipe'

import {
  ApplicationVersion,
  ExportType,
  MergedVersionState,
} from '../../entities/application.entity'
import { ToastMessageService } from '../../services/toast-message.service'
import { BranchMergeStateComponent } from '../branch-merge-state/branch-merge-state.component'
import { FlxBranchStateComponent } from '../branch-state/branch-state.component'
import {
  VersionActionEvent,
  VersionActionType,
  VersionActionsMap,
} from '../version-actions.constants'

@Component({
  selector: 'flx-version-info',
  imports: [
    NgClass,
    CdkCopyToClipboard,
    TooltipModule,
    FlxButtonModule,
    FlxIconModule,
    FlxBranchStateComponent,
    FlxUserInitialsPipe,
    FlxDateFormatPipe,
    FlxBuildTagComponent,
    BranchMergeStateComponent,
  ],
  host: { class: 'flex flex-col gap-3.5 min-h-0 flex-1' },
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './version-info.component.html',
})
export class FlxVersionInfoComponent {
  #modalService = inject(FlxModalService)
  #toastService = inject(ToastMessageService)

  applicationVersion = input.required<ApplicationVersion>()
  actions =
    input.required<
      Pick<
        VersionActionsMap,
        | VersionActionType.ExportVersion
        | VersionActionType.CompareVersions
        | VersionActionType.ViewCommitDetails
      >
    >()
  /** true renders the full commit message and description; false renders the message as a truncated button */
  showDetails = input(false)
  mergedVersionState = input<MergedVersionState | null>(null)
  actionTriggered = output<VersionActionEvent>()

  copiedTextToClipboard = signal(false)

  viewCommitDetailsAction = computed(() => this.actions()[VersionActionType.ViewCommitDetails])
  compareVersionsAction = computed(() => this.actions()[VersionActionType.CompareVersions])
  exportVersionAction = computed(() => this.actions()[VersionActionType.ExportVersion])

  onCopyToClipboardSuccess(success: boolean): void {
    if (!success) {
      this.#toastService.showErrorMessage('Failed to copy version ID to clipboard.')
      return
    }
    this.copiedTextToClipboard.set(true)
    setTimeout(() => this.copiedTextToClipboard.set(false), 2000)
  }

  onViewCommitDetails(): void {
    this.actionTriggered.emit({ action: VersionActionType.ViewCommitDetails })
  }

  onCompareVersions(): void {
    this.actionTriggered.emit({ action: VersionActionType.CompareVersions })
  }

  onExportVersion(): void {
    this.#modalService.show(ExportBinariesComponent, { backdropClass: 'overlay-backdrop' }, {
      exportType: ExportType.VERSION,
      appVersion: this.applicationVersion(),
    } as ExportModalData)
  }
}
